import { createContext, useContext, useMemo } from 'react';
import getIn from 'transmute/getIn';
import { useLocation } from 'quartz-routing/react-router';
import { getIsIframePreviewUrl } from '../utils/urls';
import { useFetchDynamicSubtotalsPreview, useFetchDynamicSubtotals, useFetchDynamicSubtotalsWithTax } from '../hooks/useDynamicSubtotals';
import { BNPL_PAYMENT_METHOD_STORAGE_KEY } from '../hooks/useHandleBnplRedirect';
import { useIframeableContext } from './IframeableContext';
import { usePaymentFormContext } from './PaymentFormContext';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultState = {
  adjustmentAmounts: [],
  lineItems: [],
  loading: true,
  error: undefined,
  currencyCode: 'USD',
  oneTimeSubtotal: 0,
  oneTimeDiscountAmount: null,
  oneTimeSubtotalDiscountAmount: null,
  oneTimeSubtotalTaxAmount: null,
  firstPaymentSubtotal: 0,
  firstPaymentSubtotalBeforeTax: 0,
  firstPaymentTaxAmount: 0,
  recurringSubtotals: [],
  firstPaymentTotal: 0,
  firstPaymentTotalInSmallestCurrencyUnit: 0,
  paymentAmountInSmallestCurrencyUnit: 0,
  discountCodeResponse: null,
  partialPaymentsSubtotals: null,
  paymentAmount: 0,
  subtotalsTaxRate: 0,
  taxBreakdowns: []
};
export const SubtotalsContext = /*#__PURE__*/createContext(defaultState);
export const useSubtotals = () => useContext(SubtotalsContext);
function getResultValues(response, lineItems, paymentMethod) {
  const subtotals = getIn(['data', 'subtotals'], response) || getIn(['previousData', 'subtotals'], response) || defaultState;
  return Object.assign({}, subtotals, {
    loading: response.loading,
    error: response.error,
    lineItems,
    paymentMethod
  });
}

// Handles fetching/formatting/returning the correct subtotal preview
// depending on if this app is loaded as a preview iframe during Payment link creation
// or during an actual checkout by a buyer.
export function useSubtotalsContextProvider(checkoutSession) {
  var _iframeableContext$pa;
  const {
    pathname
  } = useLocation();
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const currencyCode = checkoutSession.currencyCode;
  const lineItems = checkoutSession.lineItems;
  const discount = checkoutSession.orderDiscount;
  const adjustments = checkoutSession.adjustments;
  const hasCalculateAtCheckoutTaxes = checkoutSession.isAutomatedSalesTaxEnabled;
  const iframeableContext = useIframeableContext();
  const embeddedCheckoutFees = iframeableContext === null || iframeableContext === void 0 || (_iframeableContext$pa = iframeableContext.passthruProps) === null || _iframeableContext$pa === void 0 ? void 0 : _iframeableContext$pa.checkoutFees;
  const {
    formState
  } = usePaymentFormContext();
  const paymentMethod = formState.get('paymentMethod');
  const subtotalsPreviewResponse = useFetchDynamicSubtotalsPreview({
    checkoutSession,
    portalId: checkoutSession.portalId,
    currencyCode,
    lineItems,
    discount,
    adjustments,
    skip: !isIframePreviewUrl,
    paymentMethod,
    checkoutFees: embeddedCheckoutFees
  });
  const isReturningFromBnpl = Boolean(sessionStorage.getItem(BNPL_PAYMENT_METHOD_STORAGE_KEY));
  const subtotalsResponse = useFetchDynamicSubtotals({
    checkoutSession,
    skip: isIframePreviewUrl || hasCalculateAtCheckoutTaxes || isReturningFromBnpl,
    paymentMethod
  });
  const subtotalsWithTaxResponse = useFetchDynamicSubtotalsWithTax({
    checkoutSession,
    skip: isIframePreviewUrl || !hasCalculateAtCheckoutTaxes || isReturningFromBnpl,
    paymentMethod
  });
  return useMemo(() => {
    if (isIframePreviewUrl) {
      return getResultValues(subtotalsPreviewResponse, lineItems, paymentMethod);
    } else if (hasCalculateAtCheckoutTaxes) {
      return getResultValues(subtotalsWithTaxResponse, lineItems, paymentMethod);
    }
    return getResultValues(subtotalsResponse, lineItems, paymentMethod);
  }, [subtotalsResponse, subtotalsPreviewResponse, lineItems, isIframePreviewUrl, hasCalculateAtCheckoutTaxes, subtotalsWithTaxResponse, paymentMethod]);
}
export function SubtotalsProvider({
  checkoutSession,
  children
}) {
  const contextValue = useSubtotalsContextProvider(checkoutSession);
  return /*#__PURE__*/_jsx(SubtotalsContext.Provider, {
    value: contextValue,
    children: children
  });
}